import { useState, type FormEvent } from 'react';
import { ArrowUpRight, KeyRound, Lock, Plus } from 'lucide-react';
import { ConfirmDeleteButton } from '../components/confirm-delete-button';
import { toast } from 'sonner';
import {
  useAreas,
  useArchiveWorkspace,
  useCreateWorkspace,
  useTrackWorkspaceOpen,
  useWorkspaces,
  type Workspace,
} from '../lib/hooks';
import { useT } from '../lib/i18n';
import { Modal } from '../components/modal';
import { CredentialModal } from '../components/credential-modal';

const TYPE_OPTIONS: Array<{ value: Workspace['type']; label: string }> = [
  { value: 'website', label: 'Website' },
  { value: 'admin_panel', label: 'Admin panel' },
  { value: 'doc', label: 'Tài liệu / Doc' },
  { value: 'tool', label: 'Tool' },
  { value: 'folder', label: 'Folder / nhóm' },
];

export function WorkspacesPage() {
  const t = useT();
  const ws = useWorkspaces();
  const areas = useAreas();
  const createWs = useCreateWorkspace();
  const archive = useArchiveWorkspace();
  const trackOpen = useTrackWorkspaceOpen();
  const [modalOpen, setModalOpen] = useState(false);
  const [credentialModalFor, setCredentialModalFor] = useState<Workspace | null>(null);

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6 lg:p-8">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
            {t('workspaces.title')}
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Các site chị quản lý — click vào tile để mở tab mới
          </p>
        </div>
        <button type="button" className="btn-primary" onClick={() => setModalOpen(true)}>
          <Plus className="h-4 w-4" />
          {t('workspaces.add')}
        </button>
      </header>

      {ws.isLoading ? (
        <p className="text-sm text-slate-500">{t('common.loading')}</p>
      ) : ws.data && ws.data.length > 0 ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {ws.data.map((w) => (
            <a
              key={w.id}
              href={w.url}
              target="_blank"
              rel="noreferrer"
              onClick={() => trackOpen.mutate(w.id)}
              className="group glass glass-hover relative overflow-hidden p-5"
            >
              <div
                className="absolute inset-x-0 top-0 h-[3px]"
                style={{ backgroundColor: w.color ?? '#6366f1' }}
              />
              <div
                className="absolute -top-16 -right-16 h-32 w-32 rounded-full opacity-30 blur-3xl transition group-hover:opacity-60"
                style={{ backgroundColor: w.color ?? '#6366f1' }}
              />
              <div className="relative">
                <div className="mb-4 flex items-start justify-between">
                  <div
                    className="flex h-11 w-11 items-center justify-center rounded-xl text-white shadow-lg"
                    style={{
                      backgroundColor: w.color ?? '#6366f1',
                      boxShadow: `0 12px 24px -10px ${w.color ?? '#6366f1'}`,
                    }}
                  >
                    <span className="text-lg font-bold">{w.name[0]}</span>
                  </div>
                  <div className="opacity-100 md:opacity-60 md:group-hover:opacity-100 transition">
                    <ConfirmDeleteButton
                      size="md"
                      label={`Xoá "${w.name}"`}
                      disabled={archive.isPending}
                      onConfirm={() => {
                        archive.mutate(w.id, {
                          onSuccess: () => toast.success(`Đã xoá site "${w.name}"`),
                          onError: (e) =>
                            toast.error(e instanceof Error ? e.message : 'Không xoá được'),
                        });
                      }}
                    />
                  </div>
                </div>
                <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                  {w.name}
                </h3>
                <p className="mt-0.5 truncate text-xs text-slate-500">
                  {w.url.replace(/^https?:\/\//, '')}
                </p>
                <div className="mt-3 flex items-center justify-between">
                  <div className="flex flex-wrap gap-1.5">
                    <span className="badge">{w.type}</span>
                    {w.openCount > 0 && <span className="badge-info">{w.openCount} lần mở</span>}
                    {w.credentialId ? (
                      <span className="badge-success">
                        <Lock className="h-3 w-3" />
                        Vault
                      </span>
                    ) : null}
                  </div>
                  <ArrowUpRight className="h-4 w-4 text-slate-500 transition group-hover:text-slate-900 dark:group-hover:text-white" />
                </div>
                <button
                  type="button"
                  className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-slate-300 px-3 py-2 text-xs font-medium text-slate-600 transition hover:border-indigo-400 hover:bg-indigo-50 hover:text-indigo-700 dark:border-white/10 dark:text-slate-400 dark:hover:border-indigo-400/40 dark:hover:bg-indigo-500/10 dark:hover:text-indigo-300"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setCredentialModalFor(w);
                  }}
                >
                  <KeyRound className="h-3.5 w-3.5" />
                  {w.credentialId ? 'Quản lý credential' : 'Lưu credential'}
                </button>
              </div>
            </a>
          ))}
        </div>
      ) : (
        <div className="panel">
          <div className="panel-body py-12 text-center">
            <p className="text-sm text-slate-500">{t('workspaces.empty')}</p>
            <button type="button" className="btn-primary mt-4" onClick={() => setModalOpen(true)}>
              <Plus className="h-4 w-4" />
              {t('workspaces.add')}
            </button>
          </div>
        </div>
      )}

      {credentialModalFor && (
        <CredentialModal
          open={true}
          onClose={() => setCredentialModalFor(null)}
          workspaceId={credentialModalFor.id}
          workspaceName={credentialModalFor.name}
          workspaceUrl={credentialModalFor.url}
          existingCredentialId={credentialModalFor.credentialId}
        />
      )}

      <WorkspaceFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        areas={areas.data ?? []}
        onSubmit={async (input) => {
          await createWs.mutateAsync(input);
          toast.success(t('workspaces.created'));
          setModalOpen(false);
        }}
        submitting={createWs.isPending}
      />
    </div>
  );
}

function WorkspaceFormModal({
  open,
  onClose,
  areas,
  onSubmit,
  submitting,
}: {
  open: boolean;
  onClose: () => void;
  areas: Array<{ id: string; name: string }>;
  onSubmit: (input: {
    name: string;
    url: string;
    type: Workspace['type'];
    areaId: string | null;
    icon: string | null;
    color: string | null;
  }) => Promise<void>;
  submitting: boolean;
}) {
  const t = useT();
  const [name, setName] = useState('');
  const [url, setUrl] = useState('https://');
  const [type, setType] = useState<Workspace['type']>('admin_panel');
  const [areaId, setAreaId] = useState<string>('');
  const [icon, setIcon] = useState('');
  const [color, setColor] = useState('#6366f1');

  const reset = () => {
    setName('');
    setUrl('https://');
    setType('admin_panel');
    setAreaId('');
    setIcon('');
    setColor('#6366f1');
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Tên không được để trống');
      return;
    }
    try {
      new URL(url);
    } catch {
      toast.error('URL không hợp lệ');
      return;
    }
    await onSubmit({
      name: name.trim(),
      url: url.trim(),
      type,
      areaId: areaId || null,
      icon: icon.trim() || null,
      color: color || null,
    });
    reset();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={t('workspaces.add')}
      footer={
        <>
          <button type="button" onClick={handleClose} className="btn-secondary">
            {t('workspaces.form.cancel')}
          </button>
          <button type="submit" form="workspace-form" className="btn-primary" disabled={submitting}>
            {submitting ? '…' : t('workspaces.form.submit')}
          </button>
        </>
      }
    >
      <form id="workspace-form" onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="label" htmlFor="ws-name">
            {t('workspaces.form.name')}
          </label>
          <input
            id="ws-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="input"
            placeholder="VD: Asia Supermarkt — Office"
            required
            autoFocus
          />
        </div>

        <div>
          <label className="label" htmlFor="ws-url">
            {t('workspaces.form.url')}
          </label>
          <input
            id="ws-url"
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            className="input font-mono text-xs"
            placeholder="https://office.asiasupermarkt-th.de"
            required
          />
          <p className="mt-1.5 text-xs text-slate-500">{t('workspaces.form.url.help')}</p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label" htmlFor="ws-type">
              {t('workspaces.form.type')}
            </label>
            <select
              id="ws-type"
              value={type}
              onChange={(e) => setType(e.target.value as Workspace['type'])}
              className="input"
            >
              {TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value} className="bg-slate-900">
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label" htmlFor="ws-area">
              {t('workspaces.form.area')}
            </label>
            <select
              id="ws-area"
              value={areaId}
              onChange={(e) => setAreaId(e.target.value)}
              className="input"
            >
              <option value="" className="bg-slate-900">
                — Không gắn —
              </option>
              {areas.map((a) => (
                <option key={a.id} value={a.id} className="bg-slate-900">
                  {a.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label" htmlFor="ws-icon">
              {t('workspaces.form.icon')}
            </label>
            <input
              id="ws-icon"
              type="text"
              value={icon}
              onChange={(e) => setIcon(e.target.value)}
              className="input font-mono text-xs"
              placeholder="shopping-cart"
            />
          </div>
          <div>
            <label className="label" htmlFor="ws-color">
              {t('workspaces.form.color')}
            </label>
            <div className="flex items-center gap-2">
              <input
                id="ws-color"
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="h-10 w-14 cursor-pointer rounded-lg border border-slate-200 dark:border-white/10 bg-transparent"
              />
              <input
                type="text"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="input font-mono text-xs"
              />
            </div>
          </div>
        </div>
      </form>
    </Modal>
  );
}
